/**
 * Outil « Pourcentages et évolutions » (Boîte à outils, 4e → 1re).
 *
 * Pourcentage : une barre de 100 % qu'on découpe, t % de V = V × t/100.
 * Évolutions (dès la 3e, enchaînées dès la 2nde) : des hausses et des baisses
 * à la suite, chacune un coefficient multiplicateur ; le coefficient global
 * est leur produit, d'où le taux global et le taux réciproque qui ramène au
 * départ. +10 % puis −10 % ne ramènent pas au départ : l'outil le montre.
 * Calculs exacts : `percent.ts`.
 */

import { useState } from 'react';
import { schoolLevelRank } from '@jeraisonne/db/cards';
import { MathText, cn } from '@jeraisonne/ui';
import { X } from 'lucide-react';
import { SendToSheetButton } from '../../send-to-sheet-button';
import type { ToolProps } from '../../tool-types';
import type { ExactNumber } from '../../ui/exact';
import { Note, NumberField, SECONDARY_BUTTON, Segmented } from '../../ui/controls';
import { formatNumber } from '../../ui/format';
import { FIGURE_BG_CLASS } from '../../ui/svg-parts';
import {
  chain,
  coefficientLabel,
  MAX_STEPS,
  percentOf,
  RATE_MAX,
  RATE_MIN,
  rateLabel,
} from './percent';

type Mode = 'percent' | 'evolutions';

const tx = (s: string) => s.replace(/,/g, '{,}').replace(/−/g, '-');
/** dénominateur ou écriture trop longs : on n'affiche que la valeur approchée */
const tooLong = (e: ExactNumber) => {
  const den = e.plain.includes('/') ? Number(e.plain.split('/')[1]) : 1;
  return den > 1000 || (!e.plain.includes('/') && e.plain.length > 10);
};
/** « 96 », « 115,2 », « 50/3 ≈ 16,67 », ou « ≈ 104 059,36 » si trop long */
const show = (e: ExactNumber, unit = '') =>
  tooLong(e)
    ? `≈ ${formatNumber(e.value, 2)}${unit}`
    : e.plain.includes('/')
      ? `${e.plain}${unit} ≈ ${formatNumber(e.value, 2)}${unit}`
      : `${e.plain}${unit}`;
const showTex = (e: ExactNumber) =>
  tooLong(e)
    ? `\\approx ${tx(formatNumber(e.value, 2))}`
    : e.plain.includes('/')
      ? `${e.tex} \\approx ${tx(formatNumber(e.value, 2))}`
      : e.tex;
const signed = (e: ExactNumber, unit = '') => `${e.value >= 0 ? '+' : ''}${show(e, unit)}`;

export function PercentEvolutionsTool({
  onSend,
  sendCost,
  level,
}: ToolProps & { level?: string | null }) {
  const rank = schoolLevelRank(level);
  const evolutionsOk = rank === null || rank >= 4;
  const chainOk = rank === null || rank >= 5;
  const [mode, setMode] = useState<Mode>(chainOk ? 'evolutions' : 'percent');
  const [base, setBase] = useState(80);
  const [t, setT] = useState(25);
  const [start, setStart] = useState(100);
  const [rates, setRates] = useState<number[]>(chainOk ? [10, -10] : [20]);

  return (
    <div className="space-y-4">
      {evolutionsOk && (
        <Segmented
          ariaLabel="Mode"
          value={mode}
          options={[
            { value: 'percent', label: 'Pourcentage' },
            { value: 'evolutions', label: chainOk ? 'Évolutions' : 'Évolution' },
          ]}
          onChange={setMode}
        />
      )}
      {mode === 'percent' ? (
        <PercentMode
          base={base}
          setBase={setBase}
          t={t}
          setT={setT}
          onSend={onSend}
          sendCost={sendCost}
        />
      ) : (
        <EvolutionsMode
          start={start}
          setStart={setStart}
          rates={rates}
          setRates={setRates}
          maxSteps={chainOk ? MAX_STEPS : 1}
          showReciprocal={chainOk}
          onSend={onSend}
          sendCost={sendCost}
        />
      )}
    </div>
  );
}

function PercentMode({
  base,
  setBase,
  t,
  setT,
  onSend,
  sendCost,
}: {
  base: number;
  setBase: (v: number) => void;
  t: number;
  setT: (v: number) => void;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  const part = percentOf(base, t);
  return (
    <>
      <NumberField label="Quantité" value={base} onChange={setBase} min={0} max={100000} />
      <NumberField label="Pourcentage (en %)" value={t} onChange={setT} min={0} max={100} />
      <div className="space-y-1" aria-hidden>
        <div className="bg-ink-100 relative h-11 w-full overflow-hidden rounded-xl">
          <div className="bg-primary-500 absolute inset-y-0 left-0" style={{ width: `${t}%` }} />
          {Array.from({ length: 9 }, (_, i) => (
            <div
              key={i}
              className="absolute inset-y-0 w-px bg-[var(--fig-bg,#fff)] opacity-60"
              style={{ left: `${(i + 1) * 10}%` }}
            />
          ))}
        </div>
        <div className="text-ink-500 flex justify-between text-xs">
          <span>0 %</span>
          <span>50 %</span>
          <span>100 % = {base}</span>
        </div>
      </div>
      <section
        className="border-ink-100 space-y-1 rounded-2xl border px-3 py-2"
        data-testid="pe-percent"
      >
        <div className="flex flex-wrap items-center gap-2">
          <MathText className="text-ink-900 min-w-0 flex-1 text-base">
            {`$${t}\\,\\% \\text{ de } ${base} = ${base} \\times \\frac{${t}}{100} = ${showTex(part)}$`}
          </MathText>
          {onSend && (
            <SendToSheetButton
              label={part.plain}
              cost={sendCost}
              onClick={() => onSend({ atoms: part.atoms })}
            />
          )}
        </div>
        <p className="text-ink-600 text-sm">
          Prendre {t} % d’une quantité, c’est la multiplier par {t}/100 = {formatNumber(t / 100, 2)}
          .
        </p>
      </section>
    </>
  );
}

function EvolutionsMode({
  start,
  setStart,
  rates,
  setRates,
  maxSteps,
  showReciprocal,
  onSend,
  sendCost,
}: {
  start: number;
  setStart: (v: number) => void;
  rates: number[];
  setRates: (r: number[]) => void;
  maxSteps: number;
  showReciprocal: boolean;
  onSend?: ToolProps['onSend'];
  sendCost: number;
}) {
  const c = chain(start, rates);
  const maxV = Math.max(...c.values.map((v) => v.value), 1);
  const opposite = rates.length === 2 && rates[0] === -rates[1]! && rates[0] !== 0;
  const last = c.values[c.values.length - 1]!;
  return (
    <>
      <NumberField
        label="Valeur de départ"
        value={start}
        onChange={setStart}
        min={1}
        max={100000}
      />
      <div className="space-y-2">
        {rates.map((r, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="min-w-0 flex-1">
              <NumberField
                label={`Évolution ${i + 1} (en %)`}
                value={r}
                min={RATE_MIN}
                max={RATE_MAX}
                onChange={(v) => setRates(rates.map((x, j) => (j === i ? v : x)))}
              />
              <p className="text-ink-500 mt-0.5 text-center text-xs">
                {rateLabel(r)} : {coefficientLabel(r)}
              </p>
            </div>
            {rates.length > 1 && (
              <button
                type="button"
                aria-label={`Retirer l’évolution ${i + 1}`}
                onClick={() => setRates(rates.filter((_, j) => j !== i))}
                className="text-ink-400 hover:text-ink-700 flex h-11 w-11 shrink-0 items-center justify-center rounded-lg"
              >
                <X className="h-4 w-4" aria-hidden />
              </button>
            )}
          </div>
        ))}
        {rates.length < maxSteps && (
          <button
            type="button"
            className={SECONDARY_BUTTON}
            onClick={() => setRates([...rates, -10])}
          >
            Ajouter une évolution
          </button>
        )}
      </div>

      <ol
        className={cn('border-ink-100 space-y-1 rounded-2xl border p-3', FIGURE_BG_CLASS)}
        aria-label="Valeurs successives"
        data-testid="pe-chain"
      >
        {c.values.map((v, i) => (
          <li key={i} className="space-y-0.5">
            {i > 0 && (
              <p className="text-ink-500 text-xs font-semibold">
                {rateLabel(rates[i - 1]!)} : {coefficientLabel(rates[i - 1]!)}
              </p>
            )}
            <div className="flex items-center gap-2">
              <div className="bg-ink-100 h-6 min-w-0 flex-1 overflow-hidden rounded-lg">
                <div
                  className={cn('h-full rounded-lg', i === 0 ? 'bg-ink-400' : 'bg-primary-500')}
                  style={{ width: `${(v.value / maxV) * 100}%` }}
                />
              </div>
              <span className="text-ink-900 w-28 shrink-0 text-right text-sm font-semibold tabular-nums">
                {show(v)}
              </span>
            </div>
          </li>
        ))}
      </ol>

      <section
        className="border-ink-100 space-y-2 rounded-2xl border px-3 py-2"
        data-testid="pe-results"
      >
        <div className="flex flex-wrap items-center gap-2">
          <MathText className="text-ink-900 min-w-0 flex-1 text-base">
            {rates.length > 1
              ? `$\\text{CM} = ${rates.map((r) => tx(coefficientLabel(r).slice(2))).join(' \\times ')} ${tooLong(c.global) ? '' : '= '}${showTex(c.global)}$`
              : `$\\text{CM} = 1 ${rates[0]! < 0 ? '-' : '+'} \\frac{${Math.abs(rates[0]!)}}{100} = ${showTex(c.global)}$`}
          </MathText>
          {onSend && (
            <SendToSheetButton
              label={c.global.plain}
              cost={sendCost}
              onClick={() => onSend({ atoms: c.global.atoms })}
            />
          )}
        </div>
        <p className="text-ink-800 text-sm" data-testid="pe-global">
          {rates.length > 1 ? 'Évolution globale' : 'Évolution'} : {signed(c.globalRate, ' %')} — de{' '}
          {start} on arrive à {show(last)}.
        </p>
        {showReciprocal && c.reciprocal && c.reciprocalRate && (
          <div className="flex flex-wrap items-center gap-2" data-testid="pe-reciprocal">
            <p className="text-ink-800 min-w-0 flex-1 text-sm">
              Pour revenir à {start} : multiplier par 1/CM = {show(c.reciprocal)}, soit une
              évolution de {signed(c.reciprocalRate, ' %')} (taux réciproque).
            </p>
            {onSend && (
              <SendToSheetButton
                label={c.reciprocal.plain}
                cost={sendCost}
                onClick={() => onSend({ atoms: c.reciprocal!.atoms })}
              />
            )}
          </div>
        )}
      </section>

      {opposite && (
        <Note live={false}>
          {rateLabel(rates[0]!)} puis {rateLabel(rates[1]!)} ne ramènent pas au départ : les deux
          pourcentages ne portent pas sur la même valeur. CM = 1 − ({Math.abs(rates[0]!)}/100)² ={' '}
          {c.global.plain} &lt; 1.
        </Note>
      )}
    </>
  );
}
